
import type { CalculatedLoanProgram } from './mortgageUtils';

export interface LoanOption {
  id: string;
  loanType: string;
  programName: string;
  interestRate: number;
  downPayment: number;
  downPaymentPercentage: number;
  monthlyPayment: number;
  totalPayment: number;
  closingCosts: number;
  pmiRequired: boolean;
  fundingFee?: number;
  pros: string[];
  cons: string[];
  eligibilityScore: number;
  recommendationRank: number;
  // Enhanced details
  detailedMonthlyPayment?: {
    principalAndInterest: number;
    propertyTax: number;
    homeInsurance: number;
    pmi: number;
    hoa: number;
    total: number;
  };
  cashToClose?: {
    downPayment: number;
    closingCosts: number;
    prepaids: number;
    escrowAccount: number;
    fundingFee?: number;
    total: number;
  };
  qualificationAnalysis?: {
    creditScoreRequired: number;
    debtToIncomeRatio: number;
    frontEndRatio: number;
    reservesRequired: number;
    qualificationStrength: number;
  };
  loanFeatures?: {
    assumable: boolean;
    prepaymentPenalty: boolean;
    rateType: 'fixed' | 'adjustable';
    termYears: number;
    maxLoanAmount: number;
    occupancyRequirement: string;
  };
  userContext?: {
    isVeteran: boolean;
    isFirstTimeHomebuyer: boolean;
    location: { state: string; city: string };
    creditScore: number;
    homePrice: number;
  };
}

export const transformLoanProgramToLoanOption = (
  program: CalculatedLoanProgram, 
  homePrice: number = 600000,
  vaDisabilityStatus?: string | null,
  mortgageData?: any
): LoanOption => {
  // For VA 100% disability, force down payment to 0
  const actualDownPayment = (program.id.startsWith('va-') && vaDisabilityStatus === 'full') 
    ? 0 
    : program.downPayment;
  
  const actualDownPercent = (program.id.startsWith('va-') && vaDisabilityStatus === 'full') 
    ? 0 
    : program.downPercent;
    
  const loanAmount = homePrice - actualDownPayment;
  
  // Generate pros/cons based on loan type and disability status
  const pros = getLoanTypePros(program.id, vaDisabilityStatus);
  const cons = getLoanTypeCons(program.id, vaDisabilityStatus);
  
  // Calculate estimated closing costs (typically 2-5% of loan amount)
  const estimatedClosingCosts = loanAmount * 0.03; // 3% estimate
  
  // VA funding fee is waived for 100% disabled veterans
  const fundingFee = program.id.startsWith('va-') && vaDisabilityStatus !== 'full' 
    ? loanAmount * 0.023 
    : undefined;

  // Calculate detailed monthly payment breakdown using mortgageData if available
  const monthlyPropertyTax = mortgageData?.propertyTax || (homePrice * 0.012 / 12); // 1.2% annual default
  const monthlyInsurance = mortgageData?.homeInsurance || (homePrice * 0.0035 / 12); // 0.35% annual default
  const monthlyHOA = mortgageData?.hoaFees || 0;
  
  // Calculate PMI/MIP based on loan type and down payment
  let monthlyPMI = 0;
  if (actualDownPercent < 20 && !program.id.startsWith('va-')) {
    if (program.id.startsWith('fha')) {
      // FHA MIP calculation: 0.85% annually for most loans
      monthlyPMI = loanAmount * 0.0085 / 12;
    } else if (program.id.startsWith('conventional')) {
      // Conventional PMI varies by credit score and LTV
      const pmiRate = mortgageData?.creditScore >= 740 ? 0.005 : 
                     mortgageData?.creditScore >= 680 ? 0.007 : 0.01;
      monthlyPMI = loanAmount * pmiRate / 12;
    }
  }

  const detailedMonthlyPayment = {
    principalAndInterest: program.monthlyPayment,
    propertyTax: monthlyPropertyTax,
    homeInsurance: monthlyInsurance,
    pmi: monthlyPMI,
    hoa: monthlyHOA,
    total: program.monthlyPayment + monthlyPropertyTax + monthlyInsurance + monthlyPMI + monthlyHOA
  };

  // Calculate cash to close
  const prepaids = monthlyPropertyTax * 2 + monthlyInsurance * 12; // 2 months property tax, 1 year insurance
  const escrowAccount = (monthlyPropertyTax + monthlyInsurance) * 3; // 3 months for escrow
  
  const cashToClose = {
    downPayment: actualDownPayment,
    closingCosts: estimatedClosingCosts,
    prepaids: prepaids,
    escrowAccount: escrowAccount,
    fundingFee: fundingFee,
    total: actualDownPayment + estimatedClosingCosts + prepaids + escrowAccount + (fundingFee || 0)
  };

  // Calculate qualification analysis
  const grossMonthlyIncome = mortgageData?.monthlyIncome || 8000; // Default estimate
  const monthlyDebts = mortgageData?.monthlyDebts || 1200; // Default estimate
  const frontEndRatio = (detailedMonthlyPayment.total / grossMonthlyIncome) * 100;
  const backEndRatio = ((detailedMonthlyPayment.total + monthlyDebts) / grossMonthlyIncome) * 100;
  
  // Credit score requirements by loan type
  const creditRequirements = {
    'conventional': 620,
    'fha': 580,
    'va': 580,
    'usda': 640,
    'jumbo': 700
  };
  
  const requiredCreditScore = creditRequirements[program.id.split('-')[0] as keyof typeof creditRequirements] || 620;
  const qualificationStrength = Math.min(100, (mortgageData?.creditScore || 700) / requiredCreditScore * 100);

  const qualificationAnalysis = {
    creditScoreRequired: requiredCreditScore,
    debtToIncomeRatio: backEndRatio,
    frontEndRatio: frontEndRatio,
    reservesRequired: program.id.startsWith('jumbo') ? 2 : 0, // months of payments
    qualificationStrength: qualificationStrength
  };

  // Loan features by type
  const getLoanFeatures = (programId: string) => {
    if (programId.startsWith('va-')) {
      return {
        assumable: true,
        prepaymentPenalty: false,
        rateType: 'fixed' as const,
        termYears: 30,
        maxLoanAmount: 766650, // 2024 VA loan limit
        occupancyRequirement: 'Primary residence only'
      };
    } else if (programId.startsWith('fha')) {
      return {
        assumable: true,
        prepaymentPenalty: false,
        rateType: 'fixed' as const,
        termYears: 30,
        maxLoanAmount: 498257, // 2024 FHA loan limit
        occupancyRequirement: 'Owner-occupied only'
      };
    } else if (programId.startsWith('usda')) {
      return {
        assumable: false,
        prepaymentPenalty: false,
        rateType: 'fixed' as const,
        termYears: 30,
        maxLoanAmount: 0, // No limit, but income limits apply
        occupancyRequirement: 'Primary residence in eligible rural area'
      };
    } else {
      return {
        assumable: false,
        prepaymentPenalty: false,
        rateType: 'fixed' as const,
        termYears: 30,
        maxLoanAmount: 766650,
        occupancyRequirement: 'Any (primary, secondary, investment)'
      };
    }
  };

  const loanFeatures = getLoanFeatures(program.id);

  const userContext = mortgageData ? {
    isVeteran: mortgageData.isVeteran || false,
    isFirstTimeHomebuyer: mortgageData.isFirstTimeHomebuyer || false,
    location: {
      state: mortgageData.state || mortgageData.location?.state || '',
      city: mortgageData.city || mortgageData.location?.city || ''
    },
    creditScore: mortgageData.creditScore || 700,
    homePrice: homePrice
  } : undefined;
  
  return {
    id: program.id,
    loanType: program.id.startsWith('va-') ? 'va' : 
              program.id.startsWith('fha') ? 'fha' : 
              program.id.startsWith('usda') ? 'usda' : 'conventional',
    programName: program.name,
    interestRate: program.baseRate,
    downPayment: actualDownPayment,
    downPaymentPercentage: actualDownPercent,
    monthlyPayment: program.monthlyPayment,
    totalPayment: program.totalCost,
    closingCosts: estimatedClosingCosts,
    pmiRequired: actualDownPercent < 20 && !program.id.startsWith('va-'),
    fundingFee: fundingFee,
    pros: pros,
    cons: cons,
    eligibilityScore: program.eligibilityScore,
    recommendationRank: program.recommended ? 1 : program.eligibilityScore > 90 ? 2 : 3,
    detailedMonthlyPayment,
    cashToClose,
    qualificationAnalysis,
    loanFeatures,
    userContext
  };
};

// Helper functions to provide pros/cons based on loan type and disability status
const getLoanTypePros = (programId: string, vaDisabilityStatus?: string | null): string[] => {
  if (programId.startsWith('va-')) {
    const basePros = [
      'No down payment required',
      'No PMI required',
      'Competitive interest rates',
      'No prepayment penalties',
      'Reusable benefit',
      'Assumable loan'
    ];
    
    // Add special benefits for 100% disabled veterans
    if (vaDisabilityStatus === 'full') {
      return [
        ...basePros,
        'No VA funding fee (100% disability exemption)',
        'Maximum VA loan benefits available'
      ];
    }
    
    return basePros;
  } else if (programId.startsWith('fha')) {
    return [
      'Low down payment (3.5%)',
      'Lower credit score requirements (580+)',
      'Assumable loan',
      'Down payment assistance programs available',
      'Flexible debt-to-income ratios',
      'Gift funds allowed for down payment'
    ];
  } else if (programId.startsWith('usda')) {
    return [
      'No down payment required',
      'Below-market interest rates',
      'No PMI (guarantee fee instead)',
      'Rural area financing',
      'Income-based qualification',
      'Fixed and adjustable rate options'
    ];
  } else if (programId.includes('first-time')) {
    return [
      'Special first-time buyer rates',
      'Low down payment options',
      'Educational resources included',
      'Potential down payment assistance',
      'Flexible qualification criteria'
    ];
  } else {
    return [
      'Flexible terms and options',
      'No funding fee',
      'Can be used for various property types',
      'Higher loan limits available',
      'Investment property eligible',
      'Jumbo loan options'
    ];
  }
};

const getLoanTypeCons = (programId: string, vaDisabilityStatus?: string | null): string[] => {
  if (programId.startsWith('va-')) {
    const baseCons = [
      'Military service required',
      'Property must meet VA standards',
      'Primary residence only',
      'VA appraisal required',
      'Certificate of Eligibility needed'
    ];
    
    // Remove funding fee from cons if 100% disabled
    if (vaDisabilityStatus === 'full') {
      return baseCons; // No funding fee con for 100% disabled
    } else {
      return [
        ...baseCons,
        'Funding fee applies (unless disabled)'
      ];
    }
  } else if (programId.startsWith('fha')) {
    return [
      'Mortgage insurance required (upfront + annual)',
      'Lower loan limits than conventional',
      'Property standards requirements',
      'Owner-occupancy required',
      'FHA appraisal required',
      'Mortgage insurance for life of loan (in some cases)'
    ];
  } else if (programId.startsWith('usda')) {
    return [
      'Rural area requirement only',
      'Income limits apply',
      'Longer processing times',
      'Property eligibility restrictions',
      'USDA appraisal required',
      'Guarantee fee required'
    ];
  } else if (programId.includes('first-time')) {
    return [
      'First-time buyer requirement',
      'Income limits may apply',
      'Education courses required',
      'Limited program availability',
      'Property location restrictions'
    ];
  } else {
    return [
      'Higher down payment requirements',
      'PMI required with less than 20% down',
      'Stricter credit requirements',
      'Higher interest rates for some scenarios',
      'Private mortgage insurance costs',
      'Reserve requirements may apply'
    ];
  }
};
